import { Box, InputAdornment, Modal, Typography } from '@mui/material';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import Button from 'components/Common/Button';
import { useNavigate, useParams } from 'react-router-dom';
import { IoClose } from 'react-icons/io5';
import Grid from '@mui/material/Grid2';
import Spinner from 'components/Common/Spinner';
import { DataSetFormRequest, DataSetSchema } from 'utils/schema/validator/DataSetValidation';
import { zodResolver } from '@hookform/resolvers/zod';
import Radio from 'components/Common/Radio';
import { useCreateOrUpdateAgentDataSet, useGetAgentDataSet } from 'apis/business/agent/hook';
import { useGetKnowledgeBaseParser } from 'apis/business/setting/knowledgeBase/hook';

export default function WebUrlForm() {
  const navigate = useNavigate();
  const { datasetId } = useParams();

  const { data: dataset, isLoading: isDataSetLoading } = useGetAgentDataSet();
  const { mutateAsync: handleDataSet, isPending } = useCreateOrUpdateAgentDataSet();
  const { data: knowledgeBaseParser, isLoading } = useGetKnowledgeBaseParser();

  const { handleSubmit, control, watch } = useForm<DataSetFormRequest>({
    defaultValues: {
      datasource_type: 'website'
    },
    values: dataset
      ? {
          ...dataset,
          parser_config: { ...dataset.parser_config, exclude_urls: dataset?.parser_config?.exclude_urls?.join(', ') }
        }
      : {
          name: '',
          datasource_type: 'website',
          parser_config: {
            ...knowledgeBaseParser?.website,
            exclude_urls: knowledgeBaseParser?.website?.exclude_urls?.join(', ')
          }
        },
    resolver: zodResolver(DataSetSchema)
  });

  const onSubmit = (data: any) => {
    data.parser_config.exclude_urls = data?.parser_config.exclude_urls
      ? data?.parser_config.exclude_urls?.split?.(',')
      : [];
    handleDataSet(data);
  };

  if (isDataSetLoading || isLoading) {
    return <Spinner />;
  }

  return (
    <Modal
      open={true}
      onClose={() => navigate(-1)}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={style}>
        <Box
          padding='16px 24px'
          display='flex'
          justifyContent='space-between'
          borderBottom='1px solid #F3F3F3'
          alignItems='center'
          position='sticky'
          bgcolor='white'
          top={0}
          zIndex={10000}
        >
          <Typography variant='h6'>Configuration</Typography>
          <Box sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
            <IoClose />
          </Box>
        </Box>
        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Grid container padding={3}>
            <Grid size={12}>
              <Input name='name' label='Name' control={control} fullWidth required sx={{ marginTop: 0.5 }} />
            </Grid>
            {!datasetId && (
              <>
                <Grid size={12}>
                  <Radio
                    name='parser_config.scrap_website'
                    label=''
                    control={control}
                    options={[
                      { label: 'Scrap Website', value: true },
                      { label: 'Scrap Url', value: false }
                    ]}
                  />
                </Grid>
                <Grid size={12} mb={2}>
                  <Input
                    name='parser_config.url'
                    label='Url'
                    control={control}
                    placeholder='example.com'
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position='start'>https://</InputAdornment>
                      }
                    }}
                    fullWidth
                  />
                </Grid>
                {watch('parser_config.scrap_website') === true && (
                  <Grid size={12}>
                    <Input
                      name='parser_config.exclude_urls'
                      label='Exclude specific Pages'
                      placeholder='E.g. /shop/*, /blog/*, /sitemap, /contact-us'
                      control={control}
                      fullWidth
                      rows={4}
                      multiline
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>
          <Box
            display='flex'
            justifyContent='end'
            gap='16px'
            padding='16px 24px'
            borderTop='1px solid #F3F3F3'
            position='sticky'
            bottom={0}
            zIndex={100}
            bgcolor='white'
          >
            <Button
              color='secondary'
              variant='outlined'
              sx={{ border: '1px solid #F3F3F3' }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button type='submit' variant='contained' isPending={isPending}>
              {datasetId ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: '1px solid #F3F3F3',
  boxShadow: 24,
  borderRadius: '8px',
  overflow: 'auto',
  width: {
    xs: '90%',
    sm: 400,
    md: 500
  }
};
